import React, { useEffect, useState } from 'react';

/**
 * ホーム画面へのインストール導線。
 *
 * ブラウザ標準のインストール導線（Chrome のメニュー内「アプリをインストール」等）は
 * 気づかれないため、アプリ内に明示的なボタンを出す。毎日使う打刻アプリは
 * ホーム画面から1タップで開けるかどうかで定着率が変わる。
 *
 * プラットフォーム差:
 *   - Android/Chrome/Edge … `beforeinstallprompt` を捕まえて任意のタイミングで prompt() できる
 *   - iOS/Safari          … `beforeinstallprompt` が存在しない。共有メニューからの
 *                           手動操作しか無いため、手順を案内する
 *
 * インストール済みで**ブラウザのタブから**開いている場合は、同じ位置を
 * 「✓ アプリとしてインストール済み」の状態表示に差し替える。
 * インストール済み PWA を JS から起動する API は存在しない
 * （WebAPK のパッケージ名は端末ごとに生成され指定できない）ため、
 * 「アプリを起動する」のような**押しても何も起きないボタンは置かない**。
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** index.html のインラインスクリプトが早期に捕まえて保持しているイベント。 */
const storedPrompt = (): BeforeInstallPromptEvent | null =>
  (window as unknown as { __installPromptEvent?: BeforeInstallPromptEvent | null })
    .__installPromptEvent ?? null;

const clearStoredPrompt = (): void => {
  (window as unknown as { __installPromptEvent?: unknown }).__installPromptEvent = null;
};

const DISMISS_KEY = 'installPromptDismissedAt';
/**
 * インストール済みかどうかの記録。
 *
 * ブラウザのタブから開いている限り `display-mode: standalone` は false のままなので、
 * それだけでは判別できない。一方 Chrome は**インストール済みだと
 * beforeinstallprompt を発火しない**ため、「イベントが無い＝未インストール」と
 * 解釈すると、既に入れている人にインストールを勧めてしまう。
 *
 * appinstalled を受けた時点で記録し、beforeinstallprompt が来たら消す
 * （このイベントは未インストール時にしか来ない＝アンインストール検知になる）。
 */
const INSTALLED_KEY = 'pwaInstalled';
/** 辞退されたら、しばらく出さない（毎回出るとうるさい）。 */
const DISMISS_DAYS = 14;

const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS Safari のホーム画面起動判定（標準にない独自プロパティ）
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS はデスクトップ UA を名乗るため、タッチ有無で判別する
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

/** モバイル端末か。イベントが取れなくても手動手順を案内する価値があるため判定する。 */
const isMobile = (): boolean =>
  /android|iphone|ipad|ipod/i.test(window.navigator.userAgent) || isIos();

const dismissedRecently = (key: string): boolean => {
  const at = localStorage.getItem(key);
  if (!at) return false;
  const elapsed = Date.now() - Number(at);
  return Number.isFinite(elapsed) && elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

const BOX_STYLE: React.CSSProperties = {
  backgroundColor: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '8px',
  padding: '14px 16px',
  marginBottom: '16px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
};

/** iOS の共有ボタン（□に↑）。文字で説明するより実物を見せたほうが早い。 */
const ShareGlyph: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ verticalAlign: '-5px', margin: '0 3px' }}
  >
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </svg>
);

const STEP_STYLE: React.CSSProperties = { marginBottom: '4px' };
const NOTE_STYLE: React.CSSProperties = { fontSize: '12px', opacity: 0.85, marginTop: '8px' };

/**
 * ホーム画面への追加手順。
 *
 * iOS は Safari 以外だと「ホーム画面に追加」が見当たらないことがあるため、
 * その場合の逃げ道を必ず添える。
 *
 * 共有ボタンの位置は決め打ちできない。iOS 15 以降はアドレスバーを上下どちらにも
 * 置けるうえ、iPad は右上。しかもアドレスバーを上に置く設定では共有ボタンだけが
 * 下のツールバーに残るため、「アドレスバーの近く」も成立しない。
 * 言葉で位置を説明するほど長くなって読まれないので、**アイコンを大きく見せて
 * 位置は「上部か下部」だけ**に留める。スクロール中はツールバーが隠れることがあるので、
 * その復帰方法（下にスワイプ）だけ添える。
 */
const InstallHelp: React.FC<{ ios: boolean }> = ({ ios }) => {
  if (!ios) {
    return (
      <>
        <ol style={{ margin: '6px 0 0', paddingLeft: '20px', lineHeight: 1.7 }}>
          <li style={STEP_STYLE}>ブラウザ右上の「⋮」メニューを開く</li>
          <li style={STEP_STYLE}>
            「<strong>アプリをインストール</strong>」または「
            <strong>ホーム画面に追加</strong>」をタップ
          </li>
        </ol>
      </>
    );
  }

  return (
    <>
      <ol style={{ margin: '6px 0 0', paddingLeft: '20px', lineHeight: 1.7 }}>
        <li style={STEP_STYLE}>
          <strong>
            <ShareGlyph />
            このボタン
          </strong>
          をタップ
          <div style={{ fontSize: '12px', opacity: 0.85 }}>
            画面の上部か下部にあります。見えないときは画面を少し下にスワイプ
          </div>
        </li>
        <li style={STEP_STYLE}>
          「<strong>ホーム画面に追加</strong>」をタップ
        </li>
        <li style={STEP_STYLE}>右上の「追加」をタップ</li>
      </ol>
      <div style={NOTE_STYLE}>
        ※ 項目が見つからない場合は <strong>Safari</strong> で開き直してください。
        iPhone・iPad ではブラウザによって表示されないことがあります。
      </div>
    </>
  );
};

interface InstallPromptProps {
  /**
   * `banner` … ダッシュボード上部の案内。辞退すると14日間隠せる
   * `button` … ヘッダー等に常設する小さなボタン。**辞退状態を無視する**
   *
   * バナーだけだと「あとで」を押した瞬間に14日間インストールできなくなり、
   * 気が変わっても戻す手段が無い。常設の導線を必ず併設すること。
   */
  variant?: 'banner' | 'button';
}

const InstallPrompt: React.FC<InstallPromptProps> = ({ variant = 'banner' }) => {
  // マウント前に発火していた場合に備え、保持済みイベントを初期値にする
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(storedPrompt);
  const [installed, setInstalled] = useState(() => localStorage.getItem(INSTALLED_KEY) === '1');
  const [showHelp, setShowHelp] = useState(false);
  // ネイティブのインストールダイアログを一度閉じると deferred は使えなくなる。
  // 常設ボタンまで消えると復帰手段が無くなるため、状態を覚えて手動手順を案内する。
  const [consumed, setConsumed] = useState(false);
  const [dismissed, setDismissed] = useState(() => dismissedRecently(DISMISS_KEY));

  useEffect(() => {
    if (isStandalone()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      // このイベントは未インストール時にしか飛ばない＝アンインストール検知
      localStorage.removeItem(INSTALLED_KEY);
      setInstalled(false);
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onReady = () => setDeferred(storedPrompt());
    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1');
      setInstalled(true);
      setDeferred(null);
      clearStoredPrompt();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('installpromptready', onReady);
    window.addEventListener('appinstalled', onInstalled);
    // マウントが遅れて通知を取り逃していた場合の取りこぼし回収
    if (storedPrompt()) setDeferred(storedPrompt());

    // ブラウザのタブから開いている場合、standalone 判定ではインストール済みか分からない。
    // Chrome の getInstalledRelatedApps() なら実際に入っているかを問い合わせられる。
    // localStorage の記録が無い端末（この機能より前にインストールした場合）も救える。
    const nav = navigator as unknown as { getInstalledRelatedApps?: () => Promise<unknown[]> };
    if (nav.getInstalledRelatedApps) {
      nav
        .getInstalledRelatedApps()
        .then((apps) => {
          if (apps.length > 0) {
            localStorage.setItem(INSTALLED_KEY, '1');
            setInstalled(true);
          }
        })
        .catch(() => {
          /* 非対応ブラウザ等。localStorage の記録にフォールバックする */
        });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('installpromptready', onReady);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // prompt() は1つのイベントにつき1回しか使えない
    setDeferred(null);
    clearStoredPrompt();
    if (outcome === 'accepted') {
      localStorage.setItem(INSTALLED_KEY, '1');
      setInstalled(true);
      return;
    }
    if (variant === 'banner') {
      // バナーは辞退されたら引き下がる（常設ボタン側から再挑戦できる）
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      setDismissed(true);
    } else {
      // 常設ボタンは消さない。以降は手動手順を案内する
      setConsumed(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  // すでにアプリとして起動している＝案内は不要
  if (isStandalone()) return null;

  // --- インストール済み ＋ ブラウザのタブで閲覧中 ---
  // 起動を仕掛ける API は無いので、押せるボタンではなく状態表示にする。
  // 「＋ ホーム画面に追加」と同じ位置をそのまま使い、バナーは出さない
  // （毎日見る画面なので、専有面積は小さいほどよい）。
  if (installed) {
    if (variant === 'banner') return null;
    return (
      <span
        title="ホーム画面のアイコンから開くと、すぐ打刻できます"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          backgroundColor: '#f0fdf4',
          color: '#15803d',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        ✓ アプリとしてインストール済み
      </span>
    );
  }

  // --- 未インストール ---
  if (variant === 'banner' && dismissed) return null;
  // イベントが取れない環境（Chrome の判定待ち・iOS・一部ブラウザ）でも、
  // モバイルなら手動でホーム画面に追加できる。押せる先がある以上ボタンは出す。
  if (!deferred && !isMobile() && !(variant === 'button' && consumed)) return null;

  if (variant === 'button') {
    return (
      <>
        <button
          onClick={deferred ? handleInstall : () => setShowHelp((v) => !v)}
          title="ホーム画面に追加"
          style={{
            padding: '8px 12px',
            backgroundColor: 'white',
            color: '#1d4ed8',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          ＋ ホーム画面に追加
        </button>
        {showHelp && (
          <div
            style={{
              flexBasis: '100%',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              padding: '10px 14px',
              color: '#1e40af',
              fontSize: '13px',
            }}
          >
            <strong>ホーム画面への追加手順</strong>
            <InstallHelp ios={isIos()} />
          </div>
        )}
      </>
    );
  }

  return (
    <div style={BOX_STYLE}>
      <img
        src="/icon-192.png"
        alt=""
        width={40}
        height={40}
        style={{ borderRadius: '9px', flexShrink: 0 }}
      />
      <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
        <div style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '14px' }}>
          ホーム画面に追加すると便利です
        </div>
        <div style={{ color: '#1e40af', fontSize: '12px', marginTop: '2px' }}>
          アイコンから1タップで打刻できます
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={handleDismiss}
          style={{
            padding: '8px 12px',
            backgroundColor: 'transparent',
            color: '#1e40af',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
          }}
        >
          あとで
        </button>
        <button
          onClick={deferred ? handleInstall : () => setShowHelp((v) => !v)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {deferred ? '追加する' : '追加方法'}
        </button>
      </div>

      {/* iOS は beforeinstallprompt が無く、プログラムからインストールを起動できない。
          Android でもイベントが取れない場合があるため、手順は OS で出し分ける */}
      {showHelp && (
        <div style={{ flexBasis: '100%', color: '#1e40af', fontSize: '13px' }}>
          <InstallHelp ios={isIos()} />
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
